import { c as _c } from "react/compiler-runtime";
import { ConcreteEntityRightType } from "@/types/rights";
import React from "react";
import { useTranslations } from "next-intl";
import { ContentViewBlock, ContentViewBlockProps } from "@components/common/content/content-view-block";
import { Badge } from "@components/ui/badge";
import { ActivityMemberEntity, ActivityMemberInputView, ContributorFormOutputView, MemberNotifeeType, MemberNotifeeTypes } from "@features/proposals/types/member";
import { MemberNotifeeEditDialog } from "@features/proposals/ui/member/member-notifee-edit-dialog";
interface MemberNotifeeStatusProps extends Omit<ContentViewBlockProps, "title"> {
  member: ActivityMemberEntity;
  patchMember: (member: ActivityMemberEntity, body: Partial<ActivityMemberInputView>) => Promise<undefined | ActivityMemberEntity>;
}

/**
 * Component for displaying and editing activity member notification preferences.
 * Shows which notifications the member has accepted to receive.
 */
export const MemberNotifeeStatus = t0 => {
  const $ = _c(26);
  let actions;
  let member;
  let patchMember;
  let props;
  if ($[0] !== t0) {
    ({
      member,
      actions,
      patchMember,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = actions;
    $[2] = member;
    $[3] = patchMember;
    $[4] = props;
  } else {
    actions = $[1];
    member = $[2];
    patchMember = $[3];
    props = $[4];
  }
  const t = useTranslations("features.proposals.ui.memberNotifeeStatus");
  const t1 = member?.notifee;
  const t2 = member?.eventProfile?.notifee;
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      ...t1,
      ...t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const notifee = t3;
  const canPatch = member?.rights?.canPatch;
  const isMe = member?.isMe ?? true;
  let t4;
  if ($[8] !== t) {
    t4 = t("title");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("description");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== canPatch || $[13] !== isMe || $[14] !== member || $[15] !== notifee || $[16] !== patchMember) {
    t6 = MemberNotifeeTypes.map(type => {
      const notification = notifee?.[type];
      const isAccepted = notification?.accepted;
      return <NotifeeItem key={type} type={type} member={member} notification={notification} isAccepted={isAccepted ?? false} canPatch={canPatch} isMe={isMe} patchMember={patchMember} />;
    });
    $[12] = canPatch;
    $[13] = isMe;
    $[14] = member;
    $[15] = notifee;
    $[16] = patchMember;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t6) {
    t7 = <div className="flex flex-col gap-3">{t6}</div>;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== actions || $[21] !== props || $[22] !== t4 || $[23] !== t5 || $[24] !== t7) {
    t8 = <ContentViewBlock title={t4} description={t5} actions={actions} {...props}>{t7}</ContentViewBlock>;
    $[20] = actions;
    $[21] = props;
    $[22] = t4;
    $[23] = t5;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};

/**
 * Individual notification preference item with edit capability
 */
const NotifeeItem = t0 => {
  const $ = _c(43);
  const {
    type,
    member,
    notification,
    isAccepted,
    canPatch,
    isMe,
    patchMember
  } = t0;
  const tNotifications = useTranslations("features.proposals.domain.activityMember.fields.notifee");
  const t = useTranslations("features.proposals.ui.memberNotifeeStatus");
  let t1;
  if ($[0] !== canPatch || $[1] !== isMe || $[2] !== member || $[3] !== notification || $[4] !== patchMember || $[5] !== type) {
    t1 = canPatch ? <MemberNotifeeEditDialog type={type} member={member} notification={notification} isMe={isMe} patchMember={patchMember} /> : null;
    $[0] = canPatch;
    $[1] = isMe;
    $[2] = member;
    $[3] = notification;
    $[4] = patchMember;
    $[5] = type;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const editDialog = t1;
  const t2 = `types.${type}.label`;
  let t3;
  if ($[7] !== t2 || $[8] !== tNotifications) {
    t3 = tNotifications(t2);
    $[7] = t2;
    $[8] = tNotifications;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <span className="text-sm font-medium">{t3}</span>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = isAccepted ? "default" : "outline";
  let t6;
  if ($[12] !== isAccepted || $[13] !== t) {
    t6 = isAccepted ? t("accepted") : t("notAccepted");
    $[12] = isAccepted;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = <Badge variant={t5} className="ml-auto">{t6}</Badge>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== editDialog || $[19] !== t4 || $[20] !== t7) {
    t8 = <div className="flex items-center gap-2">{t4}{editDialog}{t7}</div>;
    $[18] = editDialog;
    $[19] = t4;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = `types.${type}.description`;
  let t10;
  if ($[22] !== t9 || $[23] !== tNotifications) {
    t10 = tNotifications(t9);
    $[22] = t9;
    $[23] = tNotifications;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10) {
    t11 = <div className="text-xs text-muted-foreground">{t10}</div>;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== notification || $[28] !== t) {
    t12 = notification?.acceptedAt && <div className="text-xs text-muted-foreground">{t("acceptedAt")}: {new Date(notification.acceptedAt).toLocaleDateString()}</div>;
    $[27] = notification;
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== notification || $[31] !== t) {
    t13 = notification?.acceptedByName && <div className="text-xs text-muted-foreground">{t("acceptedBy")}: {notification.acceptedByName}</div>;
    $[30] = notification;
    $[31] = t;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== isAccepted || $[34] !== notification || $[35] !== t) {
    t14 = notification?.acceptedByThirdParty && notification?.acceptedByThirdPartyReason && <div className="text-xs text-muted-foreground">{isAccepted ? t("thirdPartyReason.give") : t("thirdPartyReason.revoke")}:{" "}{notification.acceptedByThirdPartyReason}</div>;
    $[33] = isAccepted;
    $[34] = notification;
    $[35] = t;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t11 || $[38] !== t12 || $[39] !== t13 || $[40] !== t14 || $[41] !== t8) {
    t15 = <div className="flex flex-col gap-1 border-b pb-2 last:border-b-0">{t8}{t11}{t12}{t13}{t14}</div>;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
    $[40] = t14;
    $[41] = t8;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
};